import React, { useState } from 'react';
import { API_URLS } from '../config/api';
import { apiPut, showToast } from '../utils/apiUtils';
import { useNovelUpdate } from '../hooks/useNovelUpdate';
import './MusicCategory.css';

const MusicCategory = ({ novelId, novel, setNovel, refreshNovel, isProduction = false }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingMix, setEditingMix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use data from novel prop instead of local state
  const musicMixes = novel?.music_mixes || [];
  const musicAssets = novel?.assets?.musics || [];

  const { updateNovel } = useNovelUpdate(novelId, isProduction, refreshNovel);

  const handleCreateMix = async (event) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData(event.target);
    const name = formData.get('name')?.trim();
    const selectedAssets = Array.from(formData.getAll('music_assets'));

    if (!name) {
      setError('Название микса обязательно');
      setLoading(false);
      return;
    }

    if (selectedAssets.length === 0) {
      setError('Должен быть выбран хотя бы один музыкальный ресурс');
      setLoading(false);
      return;
    }

    try {
      const newMix = {
        name,
        music_asset_ids: selectedAssets
      };

      const updatedNovel = {
        ...novel,
        music_mixes: [...musicMixes, newMix]
      };

      await updateNovel(updatedNovel);
      setShowCreateForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditMix = async (event) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData(event.target);
    const name = formData.get('name')?.trim();
    const selectedAssets = Array.from(formData.getAll('music_assets'));

    if (!name) {
      setError('Название микса обязательно');
      setLoading(false);
      return;
    }

    if (selectedAssets.length === 0) {
      setError('Должен быть выбран хотя бы один музыкальный ресурс');
      setLoading(false);
      return;
    }

    try {
      const updatedMixes = musicMixes.map(mix =>
        mix.id === editingMix.id
          ? { ...mix, name, music_asset_ids: selectedAssets }
          : mix
      );

      const updatedNovel = {
        ...novel,
        music_mixes: updatedMixes
      };

      await updateNovel(updatedNovel);
      setEditingMix(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMix = async (mixId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот музыкальный микс?')) {
      return;
    }

    try {
      setLoading(true);
      const updatedMixes = musicMixes.filter(mix => mix.id !== mixId);

      const updatedNovel = {
        ...novel,
        music_mixes: updatedMixes
      };

      await updateNovel(updatedNovel);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getMusicAssetName = (assetId) => {
    const asset = musicAssets.find(a => a.id === assetId);
    return asset ? asset.name : 'Неизвестно';
  };

  const handlePopulateAllScenesWithMusic = async (mixIndex) => {
    const musicMix = musicMixes[mixIndex];

    if (!musicMix || !musicMix.music_asset_ids || musicMix.music_asset_ids.length === 0) {
      showToast('Ошибка', 'Музыкальный микс пуст. Сначала добавьте музыку.', 'error');
      return;
    }

    if (!window.confirm('Это установит этот музыкальный микс во все сцены вашего романа. Продолжить?')) {
      return;
    }

    try {
      setLoading(true);

      const baseUrl = isProduction ? API_URLS.PRODUCTION_NOVELS : API_URLS.BUILDING_NOVELS;
      await apiPut(
        `${baseUrl}/${novelId}/music-mixes/${mixIndex}/populate-scenes`,
        {},
        {},
        true,
        'Все сцены заполнены этим музыкальным миксом!'
      );

      await refreshNovel();
    } catch (error) {
      showToast('Ошибка', 'Не удалось заполнить сцены. Пожалуйста, попробуйте снова.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mc-wrap">

      {/* Create Form */}
      {showCreateForm && (
        <div className="mc-overlay" onClick={() => setShowCreateForm(false)}>
          <div className="mc-modal" onClick={e => e.stopPropagation()}>
            <h3 className="mc-modal-title">{'Добавить микс'}</h3>
            {error && <div className="mc-error">{error}</div>}
            <form onSubmit={handleCreateMix}>
              <div className="mc-form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Название микса"
                  required
                  disabled={loading}
                  className="mc-input"
                />
              </div>

              <div className="mc-form-group">
                <label className="mc-label">{'Выберите музыку'}</label>
                {musicAssets.length > 0 ? (
                  <div className="mc-assets-list">
                    {musicAssets.map((asset) => (
                      <label key={asset.id} className="mc-checkbox-item">
                        <input
                          type="checkbox"
                          name="music_assets"
                          value={asset.id}
                          disabled={loading}
                        />
                        <span className="checkbox-text">{asset.name}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="mc-no-assets">
                    {'Нет доступных музыкальных ресурсов'}
                  </div>
                )}
              </div>

              <div className="mc-modal-actions">
                <button
                  type="submit"
                  disabled={loading || musicAssets.length === 0}
                  className="mc-btn-submit"
                >
                  {loading ? 'Загрузка...' : 'Создать'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  disabled={loading}
                  className="mc-btn-cancel"
                >
                  {'Отменить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {editingMix && (
        <div className="mc-overlay" onClick={() => setEditingMix(null)}>
          <div className="mc-modal" onClick={e => e.stopPropagation()}>
            <h3 className="mc-modal-title">{'Редактировать микс'}</h3>
            {error && <div className="mc-error">{error}</div>}
            <form onSubmit={handleEditMix}>
              <div className="mc-form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Название микса"
                  defaultValue={editingMix.name}
                  required
                  disabled={loading}
                  className="mc-input"
                />
              </div>

              <div className="mc-form-group">
                <label className="mc-label">{'Выберите музыку'}</label>
                {musicAssets.length > 0 ? (
                  <div className="mc-assets-list">
                    {musicAssets.map((asset) => (
                      <label key={asset.id} className="mc-checkbox-item">
                        <input
                          type="checkbox"
                          name="music_assets"
                          value={asset.id}
                          defaultChecked={editingMix.music_asset_ids.includes(asset.id)}
                          disabled={loading}
                        />
                        <span className="checkbox-text">{asset.name}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="mc-no-assets">
                    {'Нет доступных музыкальных ресурсов'}
                  </div>
                )}
              </div>

              <div className="mc-modal-actions">
                <button
                  type="submit"
                  disabled={loading || musicAssets.length === 0}
                  className="mc-btn-submit"
                >
                  {loading ? 'Загрузка...' : 'Сохранить'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingMix(null)}
                  disabled={loading}
                  className="mc-btn-cancel"
                >
                  {'Отменить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Controls */}
      <button
        className="mc-add-btn"
        onClick={() => setShowCreateForm(true)}
        disabled={musicAssets.length === 0}
      >
        <span>🎵</span>
        {'Добавить микс'}
      </button>

      {/* Music Mixes List */}
      {musicMixes.length > 0 ? (
        <div className="mc-list">
          {musicMixes.map((mix, mixIndex) => (
            <div key={mix.id} className="mc-card">
              <div className="mc-card-head">
                <h4 className="mc-card-name">{mix.name}</h4>
                <div className="mc-card-actions">
                  <button
                    className="mc-btn-edit"
                    onClick={() => setEditingMix(mix)}
                    disabled={loading}
                  >
                    {'Редактировать'}
                  </button>
                  <button
                    className="mc-btn-delete"
                    onClick={() => handleDeleteMix(mix.id)}
                    disabled={loading}
                  >
                    {'Удалить'}
                  </button>
                </div>
              </div>
              <div className="mix-content">
                <div className="music-assets-info">
                  <strong>{'Выберите музыку'}:</strong>
                  {mix.music_asset_ids && mix.music_asset_ids.length > 0 ? (
                    <ul className="mc-tracks">
                      {mix.music_asset_ids.map((assetId) => (
                        <li key={assetId} className="mc-track">
                          🎵 {getMusicAssetName(assetId)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="mc-empty">{'Треки не выбраны'}</span>
                  )}
                </div>
                <div className="populate-section">
                  <button
                    className="mc-populate-btn"
                    onClick={() => handlePopulateAllScenesWithMusic(mixIndex)}
                    disabled={loading || !mix.music_asset_ids || mix.music_asset_ids.length === 0}
                  >
                    🌟 {'Применить ко всем сценам'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mc-no-mixes">
          <p>{'Музыкальных миксов нет'}</p>
        </div>
      )}
    </div>
  );
};

export default MusicCategory;
